import { useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, GripVertical, Plus, X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDeleteAgentGroup, useReorderAgentGroups } from "@/hooks/agent";
import { cn } from "@/lib/utils";
import { Agent } from "@/types/agent/agent";
import { AgentGroup } from "@/types/agent/agent-group";

interface GroupFilterTabsProps {
  groups: AgentGroup[];
  agents: Agent[];
  selectedGroupId: string | null;
  onGroupChange: (groupId: string | null) => void;
  onCreateGroup: () => void;
  totalCount?: number;
  serverGroupCounts?: Record<string | number, number>;
  /** Lets a caller embed the tabs inside its own toolbar row (e.g. drop the bottom rule). */
  className?: string;
}

interface SortableGroupTabProps {
  group: AgentGroup;
  count: number;
  isActive: boolean;
  onSelect: () => void;
  isTeamGroup: boolean;
}

const SortableGroupTab = ({
  group,
  count,
  isActive,
  onSelect,
  isTeamGroup,
}: SortableGroupTabProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: group.id,
  });
  const deleteGroupMutation = useDeleteAgentGroup();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  const handleDelete = () => {
    deleteGroupMutation.mutate(group.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
      },
    });
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "flex items-center gap-1 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors flex-shrink-0 group",
          isActive
            ? "border-primary text-primary bg-primary/5"
            : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50",
        )}
      >
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 -ml-1 opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity"
          title="Drag to reorder"
        >
          <GripVertical className="w-3 h-3" />
        </button>
        <button onClick={onSelect} className="flex items-center">
          {isTeamGroup ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                    {group.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent>Team group</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span>{group.name}</span>
          )}
          <span
            className={cn(
              "ml-1.5 px-1.5 py-0.5 text-xs rounded-full",
              isActive ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600",
            )}
          >
            {count}
          </span>
        </button>
        <button
          onClick={() => setShowDeleteDialog(true)}
          className={cn(
            "opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded ml-1",
            isTeamGroup ? "cursor-not-allowed opacity-50" : "hover:bg-red-50",
          )}
          title={isTeamGroup ? "Cannot delete team group" : "Delete group"}
          disabled={deleteGroupMutation.isPending || isTeamGroup}
        >
          <X
            className={cn(
              "w-3.5 h-3.5",
              isTeamGroup ? "text-gray-300" : "text-red-500 hover:text-red-700",
            )}
          />
        </button>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogTitle>Delete Group</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete "{group.name}"? All genies in this group will be moved
            to Ungrouped.
          </AlertDialogDescription>
          <div className="flex justify-end gap-3">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteGroupMutation.isPending}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleteGroupMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const GroupFilterTabs = ({
  groups,
  agents,
  selectedGroupId,
  onGroupChange,
  onCreateGroup,
  totalCount,
  serverGroupCounts,
  className,
}: GroupFilterTabsProps) => {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const reorderMutation = useReorderAgentGroups();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const groupCounts = useMemo(() => {
    const counts: Record<string | number, number> = {};
    agents.forEach((agent) => {
      const key = agent.group_id ?? "ungrouped";
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [agents]);

  const getGroupCount = (groupId: number | null): number => {
    if (serverGroupCounts) {
      const key = groupId === null ? "ungrouped" : groupId;
      return serverGroupCounts[key] ?? 0;
    }
    if (groupId === null) {
      return groupCounts["ungrouped"] ?? 0;
    }
    return groupCounts[groupId] ?? 0;
  };

  const allCount = totalCount ?? agents.length;
  const ungroupedCount = getGroupCount(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = groups.findIndex((g) => g.id === active.id);
      const newIndex = groups.findIndex((g) => g.id === over.id);
      const newOrder = arrayMove(groups, oldIndex, newIndex);
      reorderMutation.mutate(newOrder.map((g) => g.id));
    }
  };

  return (
    <div className={cn("bg-white border-b", className)}>
      {/* Mobile collapsible header */}
      <div className="md:hidden flex items-center justify-between px-4 py-2.5 border-b">
        <span className="text-sm font-medium text-gray-700">Genies Groups</span>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronDown
            className={cn(
              "w-5 h-5 text-gray-600 transition-transform",
              isCollapsed ? "" : "rotate-180",
            )}
          />
        </button>
      </div>

      {/* Desktop and expanded mobile content */}
      <div
        className={cn(
          "flex md:flex-row flex-col items-center gap-1 pb-px px-4 md:px-0 md:py-0 py-2.5",
          isCollapsed ? "hidden md:flex" : "flex md:flex",
        )}
      >
        {/* All tab - fixed position */}
        <button
          onClick={() => onGroupChange(null)}
          className={cn(
            "px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors flex-shrink-0",
            selectedGroupId === null
              ? "border-primary text-primary bg-primary/5"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50",
          )}
        >
          All
          <span
            className={cn(
              "ml-1.5 px-1.5 py-0.5 text-xs rounded-full",
              selectedGroupId === null ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600",
            )}
          >
            {allCount}
          </span>
        </button>

        {/* Sortable group tabs with wrapping */}
        <div className="flex-1 flex flex-col md:flex-row md:flex-wrap items-center gap-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={groups.map((g) => g.id)}
              strategy={horizontalListSortingStrategy}
            >
              {groups.map((group) => (
                <SortableGroupTab
                  key={group.id}
                  group={group}
                  count={getGroupCount(group.id)}
                  isActive={selectedGroupId === group.id.toString()}
                  onSelect={() => onGroupChange(group.id.toString())}
                  isTeamGroup={group.user_id !== user?.id}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Ungrouped tab - fixed position */}
          <button
            onClick={() => onGroupChange("ungrouped")}
            className={cn(
              "px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors",
              selectedGroupId === "ungrouped"
                ? "border-primary text-primary bg-primary/5"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50",
            )}
          >
            Ungrouped
            <span
              className={cn(
                "ml-1.5 px-1.5 py-0.5 text-xs rounded-full",
                selectedGroupId === "ungrouped"
                  ? "bg-primary/10 text-primary"
                  : "bg-gray-100 text-gray-600",
              )}
            >
              {ungroupedCount}
            </span>
          </button>
        </div>

        {/* Create Group Button - on right on desktop, stacked on mobile */}
        <button
          onClick={onCreateGroup}
          className="p-2 text-gray-500 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center gap-1.5 text-sm md:flex-shrink-0 w-full md:w-auto justify-center md:justify-start"
          title="Create Group"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Group</span>
        </button>
      </div>
    </div>
  );
};
